import { Disclosure } from "@design";
import { ListTree } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { siteOf } from "../Sources";
import type { ResearchPage } from "./researchModel";

/** One labelled list in the trail; `id` is its heading's, which names the list for screen readers. */
function TrailList({ heading, children }: Readonly<{ heading: string; children: React.ReactNode }>) {
  const id = useId();
  return (
    <div className="px-3.5 py-2.5">
      <Text variant="caption" tone="tertiary" id={id}>
        {heading}
      </Text>
      <ul aria-labelledby={id} className="mt-1 space-y-1">
        {children}
      </ul>
    </div>
  );
}

/**
 * What a running research has searched and read so far (MHS-798), newest first, closed to start with so the panel
 * stays one glance tall (on a phone too).
 */
export function ResearchTrail({ searches, pages }: Readonly<{ searches: string[]; pages: ResearchPage[] }>) {
  const { t } = useTranslation("chat", { keyPrefix: "research.progress.trail" });
  if (searches.length === 0 && pages.length === 0) return null;
  return (
    <Disclosure icon={<ListTree size={12} className="shrink-0" />} label={t("label")}>
      {searches.length > 0 && (
        <TrailList heading={t("searched")}>
          {[...searches].reverse().map((query) => (
            <Text key={query} as="li" variant="body-sm" tone="secondary" className="break-words">
              {query}
            </Text>
          ))}
        </TrailList>
      )}
      {pages.length > 0 && (
        <TrailList heading={t("read")}>
          {[...pages].reverse().map((page) => (
            <li key={page.url ?? page.title} className="min-w-0 break-words">
              {page.url ? (
                <a
                  href={page.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-content-secondary underline-offset-2 hover:underline"
                >
                  <Text as="span" variant="body-sm" tone="secondary">
                    {page.title || page.url}
                  </Text>
                </a>
              ) : (
                <Text as="span" variant="body-sm" tone="secondary">
                  {page.title}
                </Text>
              )}
              {page.url && (
                <Text as="span" variant="caption" tone="tertiary" className="ml-2">
                  {siteOf(page.url)}
                </Text>
              )}
            </li>
          ))}
        </TrailList>
      )}
    </Disclosure>
  );
}
